import React from 'react'
import { getProductImageRenderSpec, ImageRotation } from '../utils/ProductRenderSpec'
import { getSkinSlotPositions } from '../products/productUtils'
import { getProductImageUrl } from '../products/getProductImageUrl'
import { Spinner } from './Spinner'
import { ProductOrientation, SkinProductOption } from '../types/productTypes'
import { InflatedConfiguredInterfaceProduct } from '../list/ListTypes'
import { throwErrorInline } from '../utils/throwErrorInline'
import { logDebug } from '../debug/debugLogging'
import { ButtonImageOverflowScaleMultiplier, ButtonPercentSizeOfSkin } from './ProductImageConstants'

function createRoundedRectangle(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

async function loadImageAsync(source: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const tempImg = document.createElement('img')
    tempImg.crossOrigin = 'Anonymous'

    tempImg.onload = () => {
      resolve(tempImg)
    }

    tempImg.onerror = () => {
      reject(Error(`load failed for ${source}`))
    }

    tempImg.src = source

    if (tempImg.complete) {
      resolve(tempImg)
    }
  })
}

function isolatedCanvasOperation(
  ctx: CanvasRenderingContext2D,
  operation: (ctx: CanvasRenderingContext2D) => void,
) {
  ctx.save()
  operation(ctx)
  ctx.restore()
}

export async function renderToCanvas(
  canvas: HTMLCanvasElement,
  skinImageSrc: string,
  skinImageScale: number,
  skinRotationTurns: number,
  buttons: readonly {
    x: number
    y: number
    foregroundImageSrc: string
    backgroundImageSrc: string | null
  }[],
) {
  const ctx = canvas.getContext('2d') ?? throwErrorInline('no canvas drawing context')

  const { width: canvasWidth, height: canvasHeight } = canvas

  if (canvasHeight !== canvasWidth) {
    throw Error(`expected a square canvas, got ${canvasWidth} x ${canvasHeight}`)
  }

  const canvasSize = canvasWidth

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  ctx.clearRect(0, 0, canvasWidth, canvasHeight)

  const skinImg = await loadImageAsync(skinImageSrc)
  const btnImgs = await Promise.all(buttons.map(async item => {
    const foreground = await loadImageAsync(item.foregroundImageSrc)
    const background = item.backgroundImageSrc ? await loadImageAsync(item.backgroundImageSrc) : null

    return {
      foreground,
      background,
    }
  }))

  logDebug('canvasrenderer', 'rendering', skinImageSrc)

  isolatedCanvasOperation(ctx, () => {
    if (skinRotationTurns) {
        // translate to center
      ctx.translate(canvasWidth / 2, canvasHeight / 2)
        // rotate 1/4 turn anticlockwise
      ctx.rotate(Math.PI * 2 * skinRotationTurns)
        // reset translation
      ctx.translate(-canvasWidth / 2, -canvasHeight / 2)
    }

    const skinWidthToHeightRatio = skinImg.width / skinImg.height

    // wide
    if (skinWidthToHeightRatio > 1) {
      const height = canvasHeight / skinWidthToHeightRatio
      const offsetY = (canvasHeight - height) / 2
      ctx.drawImage(skinImg, 0, offsetY, canvasWidth, height)

    // tall or square
    } else {
      const width = canvasWidth * skinWidthToHeightRatio
      const offsetX = (canvasWidth - width) / 2
      ctx.drawImage(skinImg, offsetX, 0, width, canvasHeight)
    }
  })

  buttons.forEach((slot, slotIndex) => {
    ctx.fillStyle = 'black'
    const scale = ButtonPercentSizeOfSkin * skinImageScale
    const size = scale / 100 * canvasSize
    const imgSize = size * ButtonImageOverflowScaleMultiplier

    const x = slot.x / 100 * canvasWidth - (size / 2)
    const y = slot.y / 100 * canvasHeight - (size / 2)
    const width = size
    const height = size
    const radius = size * 24 / 100

    isolatedCanvasOperation(ctx, () => {
        // 1.5% of button size
      const spreadShadowDropDistance = 1.5 / 100 * size
        // directional spread shadow
      createRoundedRectangle(ctx, x, y + spreadShadowDropDistance, width, height, radius)
        // 5% of button size
      ctx.shadowBlur = 5 / 100 * size
      ctx.shadowColor = '#0004'
      ctx.fill()

        // ambient shadow
      createRoundedRectangle(ctx, x, y, width, height, radius)
        // 2% of button size
      ctx.shadowBlur = 2 / 100 * size
      ctx.shadowColor = '#000d'
      ctx.fill()
    })

    isolatedCanvasOperation(ctx, () => {
      createRoundedRectangle(ctx, x, y, width, height, radius)
      ctx.clip()

      const { foreground, background } = btnImgs[slotIndex];

      [background, foreground].forEach(layer => {
        if (layer != null) {
          ctx.drawImage(layer, slot.x / 100 * canvasSize - (imgSize / 2), slot.y / 100 * canvasSize - (imgSize / 2), imgSize, imgSize)
        }
      })
    })
  })

  // Base64 data output
  // NOTE: this throws a security error if not all rendered content comes from the same origin
  // const data = canvas.toDataURL()
}

interface ConfigurableProductCanvasViewProps extends SkinProductOption {
  buttonCoordsOrientation: ProductOrientation
  configuredButtons: readonly InflatedConfiguredInterfaceProduct[]
  size: number
}

export const ConfigurableProductCanvasView: React.FC<ConfigurableProductCanvasViewProps> = (props) => {
  const { skinImageRotation, imageScale, slotPositions, skinImage } = getProductImageRenderSpec(
    props.image,
    props.imageScale,
    props.orientation,
    props.imageOrientation,
    props.buttonCoordsOrientation,
    getSkinSlotPositions(props),
  )

  const { configuredButtons } = props

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)

  React.useEffect(() => {
    // const mainCanvas = canvasRef.current
    const canvas = canvasRef.current

    // to use a temporary canvas:
    // const canvas = document.createElement('canvas')
    // const renderScale = 1
    // canvas.width = props.size * renderScale
    // canvas.height = props.size * renderScale

    void (async () => {
      try {
        if (canvas) {
          await renderToCanvas(
            canvas,
            getProductImageUrl(skinImage),
            imageScale ?? 1,
            skinImageRotation === ImageRotation.QuarterTurnAnticlockwise ? -0.25 : 0,
            slotPositions.map((slot, index) => {
              const { interfaceProduct, mechProduct } = configuredButtons[index]

              return {
                ...slot,
                foregroundImageSrc: getProductImageUrl(interfaceProduct.image),
                backgroundImageSrc: getProductImageUrl(mechProduct?.image) ?? null,
              }
            }),
          )
        }

      } catch (err) {
        console.error('error rendering image', err)
      }
    })()
  }, [configuredButtons, imageScale, skinImageRotation, skinImage, slotPositions, props.size])

  return (
    <div
      style={{
        zIndex: 100,
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${props.size}px`,
        height: `${props.size}px`,
      }}
    >
      <Spinner center/>
      <canvas
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transformOrigin: 'top left',
        }}
        ref={canvasRef}
        width={props.size}
        height={props.size}
      />
    </div>
  )
}